// [機種集計]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const MODEL_REPORTS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['type', 'ppmKbn', 'ppmKbnComparison', 'sisPpmKbn'];

// 特定項目ヘッダーにおけるPPM区分、PPM区分(比較)の理想配列順
export const ORDER_LIST_FOR_PPM = ['問題児', '負け犬', '花形', '金のなる木'];

/** PPM区分がデータ上「なし」扱いになる表示値（ORDER_LIST_FOR_PPM 外で末尾に並べる） */
const PPM_KBN_OUT_OF_ORDER_VALUES: readonly string[] = ['-'];

export const normalizePpmKbnValue = (value: string): string =>
  value === '－' ? '-' : value;

/**
 * タイプ列フィルター用の選択肢。
 * settings/options の sisTypes 順に、行へ存在する値だけ残す。`-` は含めない。
 */
export const buildTypeMultiFilterSelectOptions = (
  sisTypeNames: readonly string[],
  uniqueValues: readonly string[]
): string[] => {
  const uniqueValueSet = new Set(uniqueValues);
  return sisTypeNames.filter(
    (name) => uniqueValueSet.has(name) && name !== '-'
  );
};

/**
 * 選択値のうち、現在の選択肢にあるものだけ残す。並びは選択側を維持する。
 */
export const selectedValuesWithinOptions = (
  selected: readonly string[] | undefined,
  options: readonly string[]
): string[] => {
  const allowed = new Set(options);
  return (selected ?? []).filter((value) => allowed.has(value));
};

/**
 * タイプ列追加前のお気に入りは type を持たない。
 * reducer がマージする画面では、無い type を空配列にして直前の選択を消す。
 */
export const multiFilterFromFavoriteWithType = (saved: {
  [columnName: string]: string[];
}): { [columnName: string]: string[] } => ({
  ...saved,
  type: saved.type ?? [],
});

export const columnIndexByCode = (
  columns: readonly { code: string | null }[]
): ReadonlyMap<string, number> => {
  const columnIndexMap = new Map<string, number>();
  columns.forEach((column, index) => {
    if (column.code != null && !columnIndexMap.has(column.code)) {
      columnIndexMap.set(column.code, index);
    }
  });
  return columnIndexMap;
};

export const stringCellValue = (
  columnIndexMap: ReadonlyMap<string, number>,
  row: { data: readonly { value?: string | null }[] },
  columnCode: string
): string | null | undefined => {
  const columnIndex = columnIndexMap.get(columnCode);
  if (columnIndex === undefined) {
    return undefined;
  }
  const cellValue = row.data.at(columnIndex)?.value;
  return typeof cellValue === 'string' ? cellValue : null;
};

/**
 * 複数テーブルから指定列の表示値を重複なく集める。列が無いテーブルはスキップする。
 */
export const collectUniqueColumnValues = (
  tables: readonly {
    columns: readonly { code: string | null }[];
    rows: readonly { data: readonly { value?: string | null }[] }[];
  }[],
  columnCode: string
): string[] => {
  const values = new Set<string>();
  tables.forEach((table) => {
    const columnIndex = table.columns.findIndex(
      (column) => column.code === columnCode
    );
    if (columnIndex < 0) {
      return;
    }
    table.rows.forEach((row) => {
      const cellValue = row.data.at(columnIndex)?.value;
      if (typeof cellValue === 'string') {
        values.add(cellValue);
      }
    });
  });
  return [...values];
};

/**
 * 行が列ごとの複数選択をすべて満たすか。選択が空の列と、行に無い列は条件にしない。
 */
export const rowMatchesMultiFilter = <Row>(
  row: Row,
  selectedByColumn: Readonly<Record<string, readonly string[]>> | undefined,
  getCellValue: (row: Row, columnCode: string) => string | null | undefined
): boolean => {
  for (const [columnCode, selectedValues] of Object.entries(
    selectedByColumn ?? {}
  )) {
    if (selectedValues.length === 0) {
      continue;
    }
    const cellValue = getCellValue(row, columnCode);
    // undefined は列が無い。null は列があるが値が文字列でない。
    if (cellValue === undefined) {
      continue;
    }
    if (cellValue === null || !selectedValues.includes(cellValue)) {
      return false;
    }
  }
  return true;
};

export const hasMultiFilterSelection = (
  selectedByColumn: Readonly<Record<string, readonly string[]>> | undefined
): boolean =>
  selectedByColumn != null &&
  Object.values(selectedByColumn).some((selected) => selected.length > 0);

/**
 * 先頭の平均行を残し、それ以降の行だけを複数選択で残す。
 * 選択が空、または行が無いときは入力の行をそのまま返す。
 */
export const filterRowsKeepingLeadingAverage = <Row>(
  rows: readonly Row[],
  containsAverage: boolean,
  selectedByColumn: Readonly<Record<string, readonly string[]>> | undefined,
  getCellValue: (row: Row, columnCode: string) => string | null | undefined
): Row[] => {
  if (!hasMultiFilterSelection(selectedByColumn) || rows.length === 0) {
    return [...rows];
  }

  const averageRow = containsAverage ? rows.at(0) : undefined;
  const dataRows = containsAverage ? rows.slice(1) : rows;
  const filteredDataRows = dataRows.filter((row) =>
    rowMatchesMultiFilter(row, selectedByColumn, getCellValue)
  );

  if (containsAverage && averageRow != null) {
    return [averageRow, ...filteredDataRows];
  }
  return filteredDataRows;
};

/**
 * PPM区分フィルター用の選択肢
 * ORDER_LIST_FOR_PPM 順に存在するものだけ残し、上記以外でデータに含まれる「-」「－」は `-` に正規化して末尾へ追加する。上記以外の値は含めない。
 */
export const buildPpmKbnMultiFilterSelectOptions = (
  uniqueValues: readonly string[]
): string[] => {
  const normalizedUniqueValues = uniqueValues.map(normalizePpmKbnValue);
  const ordered = ORDER_LIST_FOR_PPM.filter((label) =>
    normalizedUniqueValues.includes(label)
  );
  const extras = PPM_KBN_OUT_OF_ORDER_VALUES.filter((v) =>
    normalizedUniqueValues.includes(v)
  );
  return extras.length > 0 ? [...ordered, ...extras] : ordered;
};

// [SIS機種レポート]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const SIS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER = [
  'type',
  'sisKadoKokenWeek',
  'sisSogoKokenWeek',
  'sisPpmKbn',
];

// [機種別償却]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const DEPRECIATION_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['sisKadoKokenWeek', 'sisSogoKokenWeek'];

export const buildContributionWeekMultiFilterSelectOptions = (
  values: readonly unknown[]
): string[] => [
  ...(values.some(
    (value) => typeof value === 'string' && value.includes('(継)')
  )
    ? ['継続']
    : []),
  ...(values.some(
    (value) => typeof value === 'string' && value.includes('(終)')
  )
    ? ['終了']
    : []),
];

// [機種別償却][基板番号別データ]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const DEPRECIATION_MAINBOARD_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['sale'];

export const DEPRECIATION_KI_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['type'];

export const NON_TRANSITION_TYPE_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['type'];

// [台別データ一覧]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const UNIT_DATA_LIST_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  ['type'];

type NonTransitionRow = {
  dataForNonTransition: readonly { value?: string | null }[];
};

/**
 * 非推移列の表示値を重複なく集める。列が無いときは空配列。
 */
export const collectUniqueNonTransitionColumnValues = (
  columns: readonly { code: string | null }[],
  rows: readonly NonTransitionRow[],
  columnCode: string
): string[] => {
  const columnIndex = columns.findIndex((column) => column.code === columnCode);
  if (columnIndex < 0) {
    return [];
  }
  const values = new Set<string>();
  rows.forEach((row) => {
    const cellValue = row.dataForNonTransition.at(columnIndex)?.value;
    if (typeof cellValue === 'string') {
      values.add(cellValue);
    }
  });
  return [...values];
};

/**
 * sisTypes が無いときは行の値を `-` を除いて返す。
 */
export const typeMultiFilterSelectOptions = (
  sisTypeNames: readonly string[],
  uniqueValues: readonly string[]
): string[] =>
  sisTypeNames.length > 0
    ? buildTypeMultiFilterSelectOptions(sisTypeNames, uniqueValues)
    : uniqueValues.filter((value) => value !== '-');

export const nonTransitionStringCellValue = (
  columns: readonly { code: string | null }[],
  row: NonTransitionRow,
  columnCode: string
): string | null | undefined => {
  const columnIndex = columns.findIndex((column) => column.code === columnCode);
  if (columnIndex < 0) {
    return undefined;
  }
  const cellValue = row.dataForNonTransition.at(columnIndex)?.value;
  return typeof cellValue === 'string' ? cellValue : null;
};

// [機種別商圏]特定項目ヘッダーにおけるサブメニュー上のマルチフィルターが適用されているcolumnリスト
export const KISYOKEN_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER = [
  'type',
  'ppmKbn',
];
